import { type SessionState, state$ as session$ } from "@internal/auth";
import { reportGlobalError } from "@internal/error-handling";
import type { User } from "@supabase/supabase-js";
import { BehaviorSubject, type Subscription } from "rxjs";
import { getDb, type RecordDoc } from "@/lib/db";
import type { Profile, ProfileData } from "@/types/domain";

export interface ProfileState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
}

const internal$ = new BehaviorSubject<ProfileState>({
  user: null,
  profile: null,
  loading: true,
});

let profileSub: Subscription | null = null;
let currentUserId: string | null = null;
let initialized = false;

function stopProfileSubscription(): void {
  profileSub?.unsubscribe();
  profileSub = null;
}

async function startProfileSubscription(user: User): Promise<void> {
  const db = await getDb();
  profileSub = db.week_allowance
    .findOne({ selector: { user_id: user.id, type: "profile" } })
    .$.subscribe({
      next: (doc) => {
        if (internal$.getValue().user?.id !== user.id) return;
        const profile: Profile | null = doc
          ? {
              id: (doc.toJSON() as RecordDoc).user_id as string,
              data: (doc.toJSON() as RecordDoc).data as unknown as ProfileData,
            }
          : null;
        internal$.next({ user, profile, loading: profile === null });
      },
      error: reportGlobalError,
    });
}

async function handleSessionChange(s: SessionState): Promise<void> {
  if (s.loading) return;
  const nextUserId = s.user?.id ?? null;
  if (initialized && nextUserId === currentUserId) return;
  initialized = true;
  currentUserId = nextUserId;

  stopProfileSubscription();
  if (s.user) {
    internal$.next({ user: s.user, profile: null, loading: true });
    await startProfileSubscription(s.user);
  } else {
    internal$.next({ user: null, profile: null, loading: false });
  }
}

session$.subscribe((s) => {
  handleSessionChange(s).catch(reportGlobalError);
});

export const profile$ = internal$.asObservable();

export function getCurrentProfile(): ProfileState {
  return internal$.getValue();
}
